/**
 * "Your agent": one area at a time instead of one long open list. Tabs (skills,
 * behavior, knowledge, profile, apps) with counts; skills are compact rows that open one
 * at a time. While the builder works, the tab of the part it just changed comes to
 * the front and the changed rows are marked for a few seconds.
 * Collapsible (BuildView): collapsed it is a slim rail that still names every tab (icon,
 * name, count), so the owner sees what is inside; a change by Mo reports `onChanged` so
 * the panel opens itself.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAlignLeft, faAnglesLeft, faAnglesRight, faBan, faBookOpen, faBoxOpen, faCheck, faChevronRight, faComments,
  faEnvelopeCircleCheck, faFileArrowDown, faFileLines, faGlobe, faIdCard, faLanguage, faPen, faPlug, faPlus, faPuzzlePiece,
  faShieldHalved, faSliders, faSpinner, faStar, faTrash, faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import AddBehaviorModal from '../addBehaviorModal';
import useAiCredits, { creditsOn } from '../../hooks/useAiCredits';
import ConfirmationModal from '../confirmationModal';
import { guardWrite } from '../../utils/permissions';
import AppsTab from './AppsTab';
import { AddSkillDialog, AppLogo, EditSkillDialog, groupLogo, PackageLibraryModal, PackageSuggestion } from './PackageLibrary';
import { PLAN_LIMIT_EVENT, PlanUpgradeDialog, UsageMeter, atLimit, planLimitOf, planUsage, usePlanLimits } from './PlanLimits';
import { ProfileFields, ProfileSaveBar, useProfileForm } from './ProfileForm';
import SystemInstructions from './SystemInstructions';
import { studioApi, timeAgo } from './studioApi';
import { exportFromServer } from './skillExport';
import { load, peek } from './studioCache';
import { docsKey, fetchDocs, packagesKey } from './studioData';
import { Badge, Collapsible, EmailPreview, Empty, JsonView, Markdown, PanelTabs, Spinner, Toggle, toolConnection } from './ui';

const CATEGORIES = [
  { key: 'ton', icon: faComments },
  { key: 'laenge', icon: faAlignLeft },
  { key: 'sprache', icon: faLanguage },
  { key: 'grenzen', icon: faBan },
  { key: 'uebergabe', icon: faUserTie },
  { key: 'sonstiges', icon: faStar },
];
const TABS = ['skills', 'behavior', 'knowledge', 'profile', 'apps'];
const TAB_ICONS = { skills: faPuzzlePiece, behavior: faSliders, knowledge: faBookOpen, profile: faIdCard, apps: faPlug };
// the platform's character limits; the backend sends them with the spec (limits.py), these are the fallbacks
const LIMITS = { business_context: 1000, welcome_message: 300, welcome_messages: 5 };
// opens a tab of the panel from elsewhere in the studio (the success score's actions): { tab }
export const PANEL_TAB_EVENT = 'ub:studio-panel-tab';
const MARK_MS = 9000;

function readTab(botId) {
  try { return localStorage.getItem(`agentStudio.panel.${botId}`) || 'skills'; } catch (_) { return 'skills'; }
}

// apps whose ready-made-skill suggestion the owner closed
const dismissKey = (botId) => `agentStudio.pkgDismissed.${botId}`;
function readDismissed(botId) {
  try { return JSON.parse(localStorage.getItem(dismissKey(botId)) || '[]'); } catch (_) { return []; }
}

/** ids of skills/rules that are new or changed between two specs, and where the first change is */
function diffSpec(before, after) {
  const marks = {};
  let where = null;
  const cmp = (oldList, newList, kind) => {
    const old = new Map(oldList.map((x) => [x.id, JSON.stringify(x)]));
    for (const x of newList) {
      const o = old.get(x.id);
      if (o === undefined) marks[x.id] = 'new';
      else if (o !== JSON.stringify(x)) marks[x.id] = 'changed';
      else continue;
      where = where || kind;
    }
  };
  cmp(before.skills, after.skills, 'skills');
  cmp(before.behavior, after.behavior, 'behavior');
  const oldTools = new Map(before.tools.map((x) => [x.id, JSON.stringify(x)]));
  for (const tool of after.tools) {
    if (oldTools.get(tool.id) === JSON.stringify(tool)) continue;
    for (const sk of after.skills) {
      if (sk.tool_ids.includes(tool.id) && !marks[sk.id]) {
        marks[sk.id] = 'changed';
        where = where || 'skills';
      }
    }
  }
  if (before.company_description !== after.company_description || before.greeting !== after.greeting) {
    marks.__profile = 'changed';
    where = where || 'profile';
  }
  return { marks, where };
}

export default function AgentPanel({
  botId, spec, setSpec, knowledgeVersion, building, collapsed = false, onExpand = null, onCollapse = null, onChanged = null,
  onAskMo = null, // sends a request straight to Mo (BuildView); the skills' Edit uses it
}) {
  const aiCredits = useAiCredits(botId);
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(() => readTab(botId));
  const [openSkill, setOpenSkill] = useState(null);
  const [marks, setMarks] = useState({});
  const [docs, setDocs] = useState(() => peek(docsKey(botId)) ?? null);
  const [addingRules, setAddingRules] = useState(false); // the add-behavior modal
  const [labelling, setLabelling] = useState(false); // new rules are being titled and sorted
  // the ready-made skills library (catalog per agent)
  const [packages, setPackages] = useState(() => peek(packagesKey(botId, (i18n.language || 'en').slice(0, 2))) ?? null);
  const [library, setLibrary] = useState(null); // the open library: { provider } (null = all apps)
  const [editing, setEditing] = useState(null); // the skill whose Edit dialog is open (its shown name)
  const [addingSkill, setAddingSkill] = useState(false); // the Add skill dialog (a request to Mo)
  const [dismissed, setDismissed] = useState(() => readDismissed(botId));
  // the plan's allowance for skills and rules (PlanLimits.js); the upgrade dialog of one kind, once needed
  const planLimits = usePlanLimits(botId);
  const [upgrade, setUpgrade] = useState(null); // 'skill' | 'behavior' while the dialog is open
  const [upgradeMounted, setUpgradeMounted] = useState(false);
  const prev = useRef(null);
  const userPicked = useRef(0);
  const changed = useRef(onChanged);
  changed.current = onChanged;
  const timers = useRef([]);
  const profile = useProfileForm({
    botId, spec, setSpec, limits: { ...LIMITS, ...(spec?.limits || {}) },
  });

  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  useEffect(() => {
    const open = (e) => { if (TABS.includes(e.detail?.tab)) { userPicked.current = Date.now(); setTab(e.detail.tab); } };
    window.addEventListener(PANEL_TAB_EVENT, open);
    return () => window.removeEventListener(PANEL_TAB_EVENT, open);
  }, []);

  useEffect(() => {
    try { localStorage.setItem(`agentStudio.panel.${botId}`, tab); } catch (_) { /* storage unavailable */ }
  }, [botId, tab]);

  // Mo or the server refused because the plan's allowance is used up (useAgentBuilder: limit_reached)
  useEffect(() => {
    const open = (e) => {
      if (e.detail?.botId && e.detail.botId !== botId) return;
      setUpgradeMounted(true);
      setUpgrade(e.detail?.artifact === 'behavior' ? 'behavior' : 'skill');
    };
    window.addEventListener(PLAN_LIMIT_EVENT, open);
    return () => window.removeEventListener(PLAN_LIMIT_EVENT, open);
  }, [botId]);

  useEffect(() => {
    let alive = true;
    load(docsKey(botId), () => fetchDocs(botId)).then((d) => alive && setDocs(d)).catch(() => alive && setDocs((cur) => cur || { items: [], total: 0 }));
    return () => { alive = false; };
  }, [botId, knowledgeVersion]);

  // the library only changes with the installed packages and the connected apps
  const pkgSig = spec ? [
    ...spec.skills.filter((s) => s.package).map((s) => `${s.package.key}:${s.package.version}:${s.package.detached}`),
    '|', ...(spec.connected_apps || []),
  ].join(',') : '';
  useEffect(() => {
    if (!pkgSig) return undefined;
    let alive = true;
    load(packagesKey(botId, lang), () => studioApi.get(botId, `/packages?lang=${lang}`).then((r) => r.packages || []))
      .then((p) => alive && setPackages(p)).catch(() => alive && setPackages((cur) => cur || []));
    return () => { alive = false; };
  }, [botId, lang, pkgSig]);

  // a connected app with ready-made skills that are not added yet (and not closed by the owner)
  const suggestions = useMemo(() => {
    const groups = [];
    for (const p of packages || []) {
      if (!p.connected || p.skill_id || dismissed.includes(p.provider)) continue;
      let g = groups.find((x) => x.provider === p.provider);
      if (!g) groups.push(g = { provider: p.provider, label: p.provider_label, items: [] });
      g.items.push(p);
    }
    return groups;
  }, [packages, dismissed]);

  // what the builder just changed: bring its tab to the front, mark the rows for a moment
  useEffect(() => {
    const before = prev.current;
    prev.current = spec;
    if (!building || !before || !spec || before.id !== spec.id) return;
    const { marks: found, where } = diffSpec(before, spec);
    const keys = Object.keys(found);
    if (!keys.length) return;
    setMarks((m) => ({ ...m, ...found }));
    if (where && Date.now() - userPicked.current > 15000) setTab(where);
    changed.current?.(where);
    timers.current.push(setTimeout(() => setMarks((m) => {
      const next = { ...m };
      keys.forEach((k) => { if (next[k] === found[k]) delete next[k]; });
      return next;
    }), MARK_MS));
  }, [spec, building]);

  if (!spec) {
    return (
      <div className="as-panel-loading" role="status" aria-label={t('agent_studio_loading')}>
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }

  const openUpgrade = (artifact) => {
    setUpgradeMounted(true);
    setUpgrade(artifact);
  };
  const used = planUsage(spec);
  const skillsFull = atLimit(used.skills, planLimits?.skills);
  const rulesFull = atLimit(used.behavior, planLimits?.behavior);
  const run = async (fn) => {
    if (!guardWrite()) return false;
    setError('');
    try {
      setSpec(await fn());
      return true;
    } catch (e) {
      const hit = planLimitOf(e); // the server refused: the plan's allowance is used up
      if (hit) openUpgrade(hit.artifact);
      else setError(e.message || String(e));
      return false;
    }
  };
  const toggle = (kind, id, enabled) => run(() => studioApi.post(botId, '/toggle', { kind, id, enabled }));
  const PART_PATHS = { skill: 'skills', behavior: 'behavior', integration: 'integrations' };
  const remove = (kind, id) => run(() => studioApi.del(botId, `/${PART_PATHS[kind]}/${encodeURIComponent(id)}`));
  // a connection goes together with the tools that call through it (backend: AgentSpec.remove_integration)
  // a skill goes with the building blocks no other skill uses (backend: AgentSpec.remove_skill)
  const skillItem = (s) => {
    const n = s.tool_ids.filter((id) => spec.tools.some((x) => x.id === id)
      && !spec.skills.some((o) => o.id !== s.id && o.tool_ids.includes(id))).length;
    const key = !n ? 'agent_studio_skill_item' : n === 1 ? 'agent_studio_skill_item_tools_one' : 'agent_studio_skill_item_tools';
    return t(key, { name: s.name, n });
  };
  const integrationItem = (i) => {
    const n = spec.tools.filter((x) => x.config?.integration_id === i.id).length;
    const key = !n ? 'agent_studio_integration_item' : n === 1 ? 'agent_studio_integration_item_tools_one'
      : 'agent_studio_integration_item_tools';
    return t(key, { name: i.name, n });
  };
  const addRules = async ({ new_guidelines: texts }) => {
    setLabelling(true);
    if (await run(() => studioApi.post(botId, '/behavior', { texts }))) setTab('behavior');
    setLabelling(false);
  };
  const pick = (k) => { userPicked.current = Date.now(); setTab(k); };
  const flash = (id, mark) => {
    setMarks((m) => ({ ...m, [id]: mark }));
    timers.current.push(setTimeout(() => setMarks((m) => {
      const next = { ...m };
      if (next[id] === mark) delete next[id];
      return next;
    }), MARK_MS));
  };
  // install or configure a ready-made skill; resolves to the skill, or null
  const install = async (key, params) => {
    let skill = null;
    const ok = await run(async () => {
      const next = await studioApi.post(botId, `/packages/${encodeURIComponent(key)}?lang=${lang}`, params ? { params } : {});
      skill = next.skills.find((s) => s.package?.key === key) || null;
      return next;
    });
    return ok ? skill : null;
  };
  const addPackage = async (key) => {
    if (skillsFull && !spec.skills.some((s) => s.package?.key === key)) { // a new one would go over the plan
      setLibrary(null);
      openUpgrade('skill');
      return;
    }
    const skill = await install(key);
    if (!skill) return;
    setLibrary(null);
    setTab('skills');
    setOpenSkill(skill.id);
    flash(skill.id, 'new');
  };
  const dismiss = (provider) => {
    const next = [...new Set([...dismissed, provider])];
    setDismissed(next);
    try { localStorage.setItem(dismissKey(botId), JSON.stringify(next)); } catch (_) { /* storage unavailable */ }
  };
  // every change to a skill goes through Mo (Robert, 17.10.2026): Edit opens a dialog, its start goes to Mo
  const editWithMo = onAskMo ? (name) => { if (guardWrite()) setEditing(name); } : null;
  // a new skill too (18.10.2026); a full allowance opens the upgrade dialog instead
  const addSkill = () => {
    if (!guardWrite()) return;
    if (skillsFull) openUpgrade('skill');
    else setAddingSkill(true);
  };

  const creditsActive = creditsOn(aiCredits);
  const counts = {
    skills: spec.skills.length, behavior: spec.behavior.length, knowledge: docs ? docs.total : null, profile: null,
    apps: (spec.connected_apps || []).length || null,
  };
  const dot = (k) => (k === 'skills' && spec.skills.some((s) => marks[s.id]))
    || (k === 'behavior' && spec.behavior.some((r) => marks[r.id])) || (k === 'profile' && marks.__profile);

  return (
    <div className={`as-agent-panel${collapsed ? ' is-collapsed' : ''}`}>
      {onExpand && (
        <nav className="as-panel-rail" aria-label={t('agent_studio_your_agent')}>
          {/* the whole head opens the panel; the arrow at the inner edge, where it also sits when open */}
          <div className="as-rail-head">
            <button type="button" className="as-rail-open" onClick={() => onExpand()} aria-label={t('agent_studio_panel_open')}
              title={t('agent_studio_panel_open')}>
              <span className="as-rail-toggle" aria-hidden="true"><FontAwesomeIcon icon={faAnglesLeft} /></span>
              <span className="as-rail-title">{t('agent_studio_your_agent')}</span>
            </button>
          </div>
          {TABS.map((k) => (
            <button key={k} type="button" className="as-rail-item"
              aria-label={`${t(`agent_studio_ptab_${k}`)}${counts[k] != null ? ` ${counts[k]}` : ''}`}
              onClick={() => { pick(k); onExpand(); }}>
              <span className="as-rail-icon">
                <FontAwesomeIcon icon={TAB_ICONS[k]} />
                {dot(k) && <span className="as-tab-dot" />}
              </span>
              <span className="as-rail-label">{t(`agent_studio_ptab_${k}`)}</span>
              {counts[k] != null && <span className="as-rail-count">{counts[k]}</span>}
            </button>
          ))}
        </nav>
      )}
      <div className="as-panel-full">
      <PanelTabs active={tab} onPick={pick}
        lead={onCollapse ? ( // inside the tab bar, left of Skills (Robert, 18.10.2026)
          <button type="button" className="as-panel-collapse as-tabs-lead" onClick={onCollapse}
            aria-label={t('agent_studio_panel_close')} title={t('agent_studio_panel_close')}>
            <FontAwesomeIcon icon={faAnglesRight} />
          </button>
        ) : null}
        tabs={TABS.map((k) => ({ key: k, label: t(`agent_studio_ptab_${k}`), icon: TAB_ICONS[k], count: counts[k], dot: dot(k) }))} />
      <div className="as-panel-scroll">
        {error && <div className="as-error">{t('agent_studio_action_failed', { error })}</div>}

        {tab === 'skills' && (
          <div className="as-panel-body">
            <div className="as-panel-hint-row">
              <UsageMeter used={used.skills} limit={planLimits?.skills} onUpgrade={() => openUpgrade('skill')}
                label={t('agent_studio_usage_skills', { used: used.skills, limit: planLimits?.skills })} />
              <span className="as-panel-hint-actions">
                {spec.skills.length > 0 && (
                  <ExportButton botId={botId} label={t('agent_studio_export_all')} onError={setError} iconOnly />
                )}
                <button type="button" className="as-btn-portal is-quiet" onClick={() => setLibrary({ provider: null })}>
                  <FontAwesomeIcon icon={faBoxOpen} /> {t('agent_studio_skill_templates')}
                </button>
                {onAskMo && (
                  <button type="button" className="as-btn-portal" onClick={addSkill}>
                    <FontAwesomeIcon icon={faPlus} /> {t('agent_studio_skill_add')}
                  </button>
                )}
              </span>
            </div>
            {suggestions.map((g) => (
              <PackageSuggestion key={g.provider} group={g} onShow={() => setLibrary({ provider: g.provider })} onAdd={addPackage}
                onDismiss={() => dismiss(g.provider)} />
            ))}
            {spec.skills.length === 0 ? <Empty>{t('agent_studio_skills_empty')}</Empty> : spec.skills.map((s) => (
              <SkillRow key={s.id} skill={s} spec={spec} mark={marks[s.id]} open={openSkill === s.id}
                exportButton={<ExportButton botId={botId} skillId={s.id} label={t('agent_studio_skill_export')} onError={setError} />}
                pkg={s.package && packages?.find((p) => p.key === s.package.key)} onEdit={editWithMo}
                onOpen={() => setOpenSkill(openSkill === s.id ? null : s.id)} onToggle={(v) => toggle('skill', s.id, v)}
                onOfferToggle={(v) => toggle('skill_offer', s.id, v)}
                deleteButton={<DeleteButton label={t('agent_studio_skill_delete')}
                itemName={skillItem(s)} onDelete={() => remove('skill', s.id)} />} />
            ))}
            {editing && (
              <EditSkillDialog name={editing} busy={building} onClose={() => setEditing(null)} onStart={onAskMo} />
            )}
            {addingSkill && <AddSkillDialog busy={building} onClose={() => setAddingSkill(false)} onStart={onAskMo} />}
            {spec.integrations.length > 0 && (
              <>
                <div className="as-panel-subhead"><FontAwesomeIcon icon={faPlug} /> {t('agent_studio_integrations_title')}</div>
                {spec.integrations.map((i) => (
                  <div key={i.id} className="as-integration">
                    <div className="as-integration-head">
                      <div className="as-row-wrap">
                        <strong>{i.name}</strong>
                        <Badge>{i.kind === 'mcp' ? 'MCP' : 'API'}</Badge>
                        {i.missing_keys?.length > 0
                          ? <Badge tone="amber">{t('agent_studio_key_missing')}</Badge>
                          : i.status === 'error' ? <Badge tone="red" title={i.status_detail}>{t('agent_studio_error')}</Badge>
                            : <Badge tone="green">{t('agent_studio_connected')}</Badge>}
                      </div>
                      <DeleteButton label={t('agent_studio_integration_delete')} itemName={integrationItem(i)}
                        onDelete={() => remove('integration', i.id)} />
                    </div>
                    {i.description && <p className="as-small as-muted">{i.description}</p>}
                    {i.missing_keys?.length > 0 && <p className="as-small as-warn-text">{t('agent_studio_key_missing_hint')}</p>}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {tab === 'behavior' && (
          <div className="as-panel-body">
            <div className="as-panel-hint-row">
              <UsageMeter used={used.behavior} limit={planLimits?.behavior} onUpgrade={() => openUpgrade('behavior')}
                label={t('agent_studio_usage_rules', { used: used.behavior, limit: planLimits?.behavior })} />
              <span className="as-panel-hint-actions">
                <button type="button" className="as-btn-portal" disabled={labelling}
                  onClick={() => guardWrite() && (rulesFull ? openUpgrade('behavior') : setAddingRules(true))}>
                  <FontAwesomeIcon icon={faPlus} /> {t('add_behavior')}
                </button>
              </span>
            </div>
            <section className="as-rule-section" aria-label={t('agent_studio_rules_title')}>
              <div className="as-rule-section-head">
                <span className="as-rule-section-title">{t('agent_studio_rules_title')}</span>
                {spec.behavior.length > 0 && (
                  <span className="as-rule-section-count">
                    {t('agent_studio_sysinstr_count', { on: spec.behavior.filter((b) => b.enabled).length, total: spec.behavior.length })}
                  </span>
                )}
              </div>
              <p className="as-panel-hint">{t('agent_studio_rules_hint')}</p>
              {labelling && <div className="as-small as-muted as-row-wrap"><Spinner size={12} /> {t('agent_studio_rules_adding')}</div>}
              {spec.behavior.length === 0 ? <Empty>{t('agent_studio_behavior_empty')}</Empty> : (
                <div className="as-rules-card">
                  {CATEGORIES.map((c) => {
                    const rules = spec.behavior.filter((b) => (b.category || 'sonstiges') === c.key);
                    if (!rules.length) return null;
                    return (
                      <div key={c.key} className="as-rule-group">
                        <div className="as-rule-cat"><FontAwesomeIcon icon={c.icon} /> {t(`agent_studio_cat_${c.key}`)}</div>
                        {rules.map((r) => (
                          <div key={r.id} className={`as-rule ${r.enabled ? '' : 'is-off'} ${marks[r.id] ? 'is-marked' : ''}`}>
                            <Collapsible
                              title={<span className="as-rule-title">{r.title}{marks[r.id] && <MarkBadge mark={marks[r.id]} />}</span>}
                              right={(
                                <span className="as-row-actions">
                                  <Toggle checked={r.enabled} onChange={(v) => toggle('behavior', r.id, v)} />
                                  <DeleteButton label={t('agent_studio_rule_delete')}
                                    itemName={t('agent_studio_rule_item', { name: r.title })}
                                    onDelete={() => remove('behavior', r.id)} />
                                </span>
                              )}
                            >
                              <p className="as-rule-text">{r.instruction}</p>
                            </Collapsible>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
            <SystemInstructions botId={botId} spec={spec} onToggle={(id, v) => toggle('system_instruction', id, v)} />
            <AddBehaviorModal open={addingRules} onClose={() => setAddingRules(false)} onSubmit={addRules} botId={botId}
              remainingSlots={planLimits?.behavior != null ? Math.max(0, Math.min(10, planLimits.behavior - used.behavior)) : 10} />
          </div>
        )}

        {tab === 'knowledge' && (
          <div className="as-panel-body">
            <div className="as-panel-hint-row">
              <p className="as-panel-hint">{t('agent_studio_knowledge_hint')}</p>
              <Link to="/knowledge/website-links" className="as-btn-portal">
                <FontAwesomeIcon icon={faBookOpen} /> {t('agent_studio_knowledge_manage')}
              </Link>
            </div>
            <KnowledgeList docs={docs} />
          </div>
        )}

        {tab === 'profile' && (
          <div className="as-panel-body">
            {marks.__profile && <p className="as-panel-hint"><MarkBadge mark="changed" /></p>}
            <ProfileFields form={profile} spec={spec} botId={botId} creditsActive={creditsActive} />
          </div>
        )}

        {tab === 'apps' && (
          <AppsTab botId={botId} packages={packages} onOpenLibrary={(provider) => setLibrary({ provider, single: true })} />
        )}
      </div>
      {tab === 'profile' && <ProfileSaveBar form={profile} />}
      </div>
      {/* outside the tabs: opened from Skills ("Ready-made skills") and from Apps (an app's templates) */}
      <PackageLibraryModal open={!!library} onClose={() => setLibrary(null)} packages={packages} onInstall={addPackage}
        botId={botId} initialProvider={library?.provider || null} single={!!library?.single} />
      {upgradeMounted && (
        <PlanUpgradeDialog open={!!upgrade} artifact={upgrade} onClose={() => setUpgrade(null)}
          used={upgrade === 'behavior' ? used.behavior : used.skills}
          limit={(upgrade === 'behavior' ? planLimits?.behavior : planLimits?.skills) ?? 0} />
      )}
    </div>
  );
}

/** Trash icon right of a skill's / rule's toggle; asks with the platform's confirmation
 *  modal. On success the row disappears; an error shows at the top of the panel. */
function DeleteButton({ label, itemName, onDelete }) {
  const [asking, setAsking] = useState(false);
  return (
    <>
      <button type="button" className="as-trash-btn" title={label} aria-label={label}
        onClick={(e) => { e.stopPropagation(); if (guardWrite()) setAsking(true); }}>
        <FontAwesomeIcon icon={faTrash} />
      </button>
      <ConfirmationModal open={asking} onClose={() => setAsking(false)} onConfirm={onDelete} itemName={itemName} />
    </>
  );
}

function MarkBadge({ mark }) {
  const { t } = useTranslation();
  return <span className="as-mark">{mark === 'new' ? t('agent_studio_badge_new') : t('agent_studio_badge_changed')}</span>;
}

function EditButton({ onClick }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="as-btn-portal" onClick={onClick}>
      <FontAwesomeIcon icon={faPen} /> {t('edit')}
    </button>
  );
}

/** Downloads one skill (skillId) or the whole agent (no skillId) as JSON (skillExport.js); read-only. */
function ExportButton({ botId, skillId = null, label, onError, iconOnly = false }) {
  const [busy, setBusy] = useState(false);
  const click = async (e) => {
    e.stopPropagation();
    setBusy(true);
    onError('');
    try {
      await exportFromServer(botId, skillId);
    } catch (err) {
      onError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className={`as-btn-portal is-quiet${iconOnly ? ' is-icon' : ''}`} onClick={click} disabled={busy}
      aria-label={label} title={label}>
      <FontAwesomeIcon icon={busy ? faSpinner : faFileArrowDown} spin={busy} />{!iconOnly && ` ${label}`}
    </button>
  );
}

function SkillRow({ skill, spec, mark, open, pkg, onOpen, onToggle, onOfferToggle, onEdit, deleteButton, exportButton = null }) {
  const { t } = useTranslation();
  const tools = skill.tool_ids.map((id) => spec.tools.find((x) => x.id === id)).filter(Boolean);
  // a ready-made skill is our tested template; the owner changes it (like every skill) by telling Mo (Edit)
  const fromTemplate = !!skill.package && !skill.package.detached;
  const tested = !fromTemplate && tools.length > 0 && tools.every((x) => x.last_test?.ok);
  const failed = tools.some((x) => x.last_test && !x.last_test.ok);
  // Robert, 17.10.2026: the app's logo (Wix Bookings, Wix CRM …) and the title on one line, the summary below;
  // the name's "(Wix Bookings)" is left out, the logo says it
  const app = skill.package && pkg ? (pkg.group_label || pkg.provider_label || '') : '';
  const name = app && skill.name.endsWith(` (${app})`) ? skill.name.slice(0, -(app.length + 3)) : skill.name;
  const badges = [
    mark && <MarkBadge key="mark" mark={mark} />,
    fromTemplate && pkg?.update_available && <Badge key="update" tone="blue">{t('agent_studio_pkg_update_badge')}</Badge>,
    tested && <Badge key="tested" tone="brand"><FontAwesomeIcon icon={faCheck} /> {t('agent_studio_tested')}</Badge>,
    failed && <Badge key="failed" tone="amber">{t('agent_studio_test_failed')}</Badge>,
    skill.status_note && <Badge key="note" tone="amber">{skill.status_note}</Badge>,
    skill.counts_toward_plan === false && (
      <Badge key="included" title={t('agent_studio_skill_included_hint')}>{t('agent_studio_skill_included')}</Badge>
    ),
  ].filter(Boolean);
  return (
    <div className={`as-skill ${skill.enabled ? '' : 'is-off'} ${open ? 'is-open' : ''} ${mark ? 'is-marked' : ''}`}>
      <div className="as-skill-head">
        <button type="button" className="as-skill-open" onClick={onOpen} aria-expanded={open}>
          <FontAwesomeIcon icon={faChevronRight} className={`as-chevron ${open ? 'is-open' : ''}`} />
          <span className="as-skill-main">
            <span className="as-skill-title">
              {app && (
                <span className="as-skill-app" title={app} role="img" aria-label={app}>
                  <AppLogo provider={pkg.provider} src={groupLogo(pkg.provider, pkg.group)} size="xs" />
                </span>
              )}
              <span className="as-skill-name">{name}</span>
            </span>
            {badges.length > 0 && <span className="as-row-wrap as-skill-meta">{badges}</span>}
            {skill.summary && <span className={`as-skill-summary ${open ? '' : 'as-clamp-2'}`}>{skill.summary}</span>}
          </span>
        </button>
        <span className="as-row-actions">
          <Toggle checked={skill.enabled} onChange={onToggle} />
          {deleteButton}
        </span>
      </div>

      {open && (
        <div className="as-skill-body">
          {(onEdit || exportButton) && (
            <div className="as-skill-edit">
              {exportButton}
              {onEdit && <EditButton onClick={() => onEdit(name)} />}
            </div>
          )}
          {skill.captures_leads && (
            // a lead skill offers itself when the agent cannot answer (backend lead_offer.py, Robert 18.10.2026)
            <div className="as-skill-offer">
              <div className="as-skill-offer-text">
                <span className="as-skill-offer-label">{t('agent_studio_skill_offer_label')}</span>
                <span className="as-small as-muted">{t('agent_studio_skill_offer_hint')}</span>
              </div>
              <Toggle checked={skill.offer_proactively !== false} disabled={!skill.enabled}
                title={t('agent_studio_skill_offer_label')} onChange={onOfferToggle} />
            </div>
          )}
          {fromTemplate && pkg?.needs_verification && (
            <p className="as-small as-pkg-verify"><FontAwesomeIcon icon={faEnvelopeCircleCheck} /> {t('agent_studio_pkg_verifies')}</p>
          )}
          {skill.rules?.length > 0 && (
            <div className="as-skill-block">
              <div className="as-skill-label"><FontAwesomeIcon icon={faShieldHalved} /> {t('agent_studio_rules')}</div>
              <ul className="as-skill-rules">
                {skill.rules.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {(tools.length > 0 || skill.uses_knowledge) && (
            <div className="as-skill-block">
              <div className="as-skill-label"><FontAwesomeIcon icon={faPlug} /> {t('agent_studio_uses')}</div>
              <div className="as-chips">
                {skill.uses_knowledge && <span className="as-chip"><FontAwesomeIcon icon={faBookOpen} /> {t('agent_studio_knowledge_title')}</span>}
                {tools.map((x) => {
                  const c = toolConnection(x, spec.integrations, t);
                  return (
                    <span key={x.id} className="as-chip" title={x.description}>
                      <FontAwesomeIcon icon={c.icon} /> <span className="as-muted">{c.label}:</span> {c.detail}
                      {x.guard_code && <FontAwesomeIcon icon={faShieldHalved} className="as-brand" />}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {skill.instructions && (
            <div className="as-skill-block">
              <div className="as-skill-label">{t('agent_studio_procedure')}</div>
              <div className="as-procedure"><Markdown text={skill.instructions} /></div>
            </div>
          )}
          {skill.examples?.length > 0 && (
            <div className="as-skill-block">
              <div className="as-skill-label">{t('agent_studio_examples')}</div>
              <div className="as-chips">{skill.examples.map((e, i) => <span key={i} className="as-chip">"{e}"</span>)}</div>
            </div>
          )}
          {tools.map((x) => (
            <div key={x.id} className="as-tool">
              <div className="as-row-wrap">
                <strong>{x.name}</strong>
                {x.requires_confirmation && <Badge tone="blue">{t('agent_studio_tool_confirms')}</Badge>}
                {x.verbatim_reply && <Badge tone="blue">{t('agent_studio_tool_verbatim')}</Badge>}
                {x.guard_code && <Badge tone="brand">{t('agent_studio_tool_guard')}</Badge>}
                {x.kind === 'app_action' && !x.last_test && <Badge tone="green">{t('agent_studio_tool_app_action')}</Badge>}
                {x.last_test
                  ? (x.last_test.ok ? <Badge tone="green">{t('agent_studio_tool_test_ok')}</Badge>
                    : <Badge tone="amber" title={x.last_test.summary}>{t('agent_studio_test_failed')}</Badge>)
                  : x.kind !== 'app_action' && <Badge>{t('agent_studio_tool_untested')}</Badge>}
              </div>
              <p className="as-small as-muted">{x.description}</p>
              {x.config?.code && (
                <Collapsible title={<span className="as-small as-muted">{t('agent_studio_view_code')}</span>}>
                  <JsonView value={x.config.code} />
                </Collapsible>
              )}
              {x.guard_code && (
                <Collapsible title={<span className="as-small as-muted">{t('agent_studio_view_guard')}</span>}>
                  <JsonView value={x.guard_code} />
                </Collapsible>
              )}
              {x.kind === 'http' && (
                <Collapsible title={<span className="as-small as-muted">{t('agent_studio_view_call')}</span>}>
                  <JsonView value={x.config} />
                </Collapsible>
              )}
              {x.kind === 'send_email' && (
                <Collapsible title={<span className="as-small as-muted">{t('agent_studio_view_email')}</span>}>
                  <EmailPreview config={x.config} t={t} />
                </Collapsible>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KnowledgeList({ docs }) {
  const { t, i18n } = useTranslation();
  const [all, setAll] = useState(false);
  if (docs === null) return <div className="as-small as-muted">{t('agent_studio_loading')}</div>;
  const { items } = docs;
  const shown = all ? items : items.slice(0, 15);
  return (
    <>
      {items.length === 0 ? <Empty>{t('agent_studio_knowledge_empty')}</Empty> : (
        <ul className="as-doc-list">
          {shown.map((d) => (
            <li key={d.id}>
              <FontAwesomeIcon icon={d.url ? faGlobe : faFileLines} className="as-doc-ic" />
              <div className="as-doc-main">
                <div className="as-doc-title" title={d.title}>{d.title}</div>
                <div className="as-small as-muted">
                  {d.status && d.status !== 'completed' ? `${t(`agent_studio_doc_${d.status}`, { defaultValue: d.status })} · ` : ''}
                  {timeAgo(d.created_at, t, i18n.language)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {items.length > 15 && (
        <button type="button" className="as-btn-link as-small" onClick={() => setAll((a) => !a)}>
          {all ? t('agent_studio_show_less') : t('agent_studio_knowledge_more', { n: items.length - 15 })}
        </button>
      )}
    </>
  );
}
